import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import useTheme from '../../utils/useTheme';
import usePrevious from '../../utils/usePrevious';
import { Avatar } from '../Avatar/Avatar';
import { Icon } from '../Icon/Icon';
import {
  AvatarWrapper,
  MessageElement,
  NotificationHead,
  NotificationElement,
  NotificationIcon,
  NotificationVariation,
  getVariationColor,
  getVariationIcon,
  NotificationBody,
} from './Notification.styles';
import { Button } from '../Button/Button';
import { SvgIcon } from '../SvgIcons/svgIcons';

export interface IProps {
  /** One of "normal" (default), "secondary" or "error" */
  variation?: NotificationVariation;
  /** Event fired when the notification button is clicked */
  onChange?: (isOpen?: boolean) => void;
  /** Whether the notification is open or not. Defaults to false. */
  isOpen?: boolean;
  /** Delay in milliseconds - defaults to 5000. Set to 0 to disable delay */
  delay?: number;
  /** Whether to allow the user to close the notification via a close button.  Defaults to true. */
  closable?: boolean;
  /** Set if the alert box has an expandable/collapsible content section */
  collapsible?: boolean;
  /** Set true if the alert box should be default collpased - works with collapsible prop */
  collapsed?: boolean;
  /** Expandable/collapsible content that appears below the main content (children) */
  collapsibleDescription?: string | ReactNode;
  /** The toggle collapse button label when the alert is in collapsed state, default: "Read more" */
  collapseButtonLabelOpen?: string | ReactNode;
  /** The toggle collapse button label when the alert is in expanded state, default: "Minimise" */
  collapseButtonLabelClose?: string | ReactNode;
}

export const Notification: FC<IProps> = ({
  children,
  variation,
  onChange,
  isOpen,
  delay = 5000,
  closable = true,
  collapsible = false,
  collapsed = false,
  collapsibleDescription = undefined,
  collapseButtonLabelOpen = 'Read more',
  collapseButtonLabelClose = 'Minimise',
}) => {
  const theme = useTheme();
  const prevIsOpen = usePrevious(isOpen);
  const timer = useRef<number>(null);
  const [isCollapsed, setIsCollapsed] = useState(collapsed);
  const isCollapsible = collapsible && !!collapsibleDescription;

  const clearTimer = () => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };

  useEffect(() => {
    if (delay > 0) {
      if (isOpen) {
        if (!prevIsOpen || timer.current === null) {
          clearTimer();
          timer.current = window.setTimeout(() => {
            clearTimer();
            if (onChange) {
              onChange(false);
            }
          }, delay);
        }
      } else {
        clearTimer();
      }
    }

    return clearTimer;
  }, [delay, isOpen, prevIsOpen, onChange]);

  const handleClose = () => {
    clearTimer();
    if (onChange) {
      onChange(false);
    }
  };

  const handleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const angleIcon = isCollapsed ? 'angle-down' : 'angle-up';

  return (
    <NotificationElement
      isOpen={isOpen}
      variation={variation}
      data-testid="NotificationElement"
      role={variation === 'error' ? 'alert' : 'status'}
      aria-live={variation === 'error' ? 'assertive' : 'polite'}
    >
      <div>
        <NotificationHead>
          <AvatarWrapper collapsible={isCollapsible}>
            <Avatar
              icon={getVariationIcon(variation)}
              bg={getVariationColor(variation, theme)}
              color={theme.vars.newColor.white}
            />
          </AvatarWrapper>
          <MessageElement>{children}</MessageElement>
          {isCollapsible && (
            <div>
              <Button
                variation="tertiary"
                rightSideIcon={<SvgIcon name={angleIcon} size="16" />}
                onClick={handleCollapse}
                data-testid="CollapseButton"
              >
                {isCollapsed && collapseButtonLabelOpen}
                {!isCollapsed && collapseButtonLabelClose}
              </Button>
            </div>
          )}
          {closable && (
            <NotificationIcon onClick={handleClose} data-testid="CloseButton">
              <Icon icon="times" />
            </NotificationIcon>
          )}
        </NotificationHead>
      </div>
      {collapsibleDescription && (!isCollapsed || !isCollapsible) && (
        <NotificationBody>{collapsibleDescription}</NotificationBody>
      )}
    </NotificationElement>
  );
};
